import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import Card from "../../components/common/Card";
import Modal from "../../components/common/Modal";
import Loader from "../../components/common/Loader";
import RequestDocumentPreview from "./RequestDocumentPreview";
import EmptyState from "../../components/common/EmptyState";
import { color } from "../../styles/theme";
import { listRoomAccessRequests, getRoomAccessRequest, approveRoomAccessRequest, rejectRoomAccessRequest,
  getRoomRequestSettings, setRoomRequestSettings, downloadRoomRequestDocument } from "../../services/nonPublicRoomRequestService";

export default function NonPublicAccessRequests() {
  const { t, i18n } = useTranslation("roomAccess");
  const { isSubAccount } = useAuth();
  const [status, setStatus] = useState("Pending");
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsError, setSettingsError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError("");
    listRoomAccessRequests({ Status: status || undefined, PageNumber: page, PageSize: 20 }, controller.signal)
      .then(({ data }) => { if (!controller.signal.aborted) { setRows(data.Result || []); setTotal(data.Metadata?.TotalCount || 0); } })
      .catch((err) => { if (!controller.signal.aborted) setError(err.response?.data?.Message || t("loadFailed")); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [status, page, refresh, t]);

  useEffect(() => {
    if (isSubAccount) return;
    const controller = new AbortController();
    getRoomRequestSettings(controller.signal).then(({ data }) => {
      if (!controller.signal.aborted) setReminder(data.Result.IsNonPublicRequestReminderEnabled);
    }).catch((err) => { if (!controller.signal.aborted && err.response?.status !== 403) setSettingsError(t("settingsFailed")); });
    return () => controller.abort();
  }, [isSubAccount, t]);

  useEffect(() => {
    setDetail(null); setDetailError(""); setDecision(null); setReason(""); setPreviewOpen(false);
    if (selectedId == null) return;
    const controller = new AbortController();
    getRoomAccessRequest(selectedId, controller.signal).then(({ data }) => {
      if (!controller.signal.aborted) setDetail(data.Result);
    }).catch((err) => { if (!controller.signal.aborted) setDetailError(err.response?.data?.Message || t("loadFailed")); });
    return () => controller.abort();
  }, [selectedId, t]);

  const formatDate = (value) => value ? new Date(value).toLocaleString(i18n.language || "en") : "—";
  const changeReminder = async () => {
    setSettingsBusy(true); setSettingsError("");
    try { const { data } = await setRoomRequestSettings(!reminder); setReminder(data.Result.IsNonPublicRequestReminderEnabled); }
    catch (err) { setSettingsError(err.response?.data?.Message || t("settingsFailed")); }
    finally { setSettingsBusy(false); }
  };
  const download = async () => {
    setBusy(true);
    try {
      const response = await downloadRoomRequestDocument(detail.Id);
      const extension = { "application/pdf": "pdf", "image/png": "png", "image/jpeg": "jpg" }[response.data.type?.split(";")[0]] || "bin";
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a"); link.href = url; link.download = `access-request-${detail.Id}.${extension}`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(t("downloadFailed")); }
    finally { setBusy(false); }
  };
  const process = async () => {
    setBusy(true);
    try {
      if (decision === "approve") await approveRoomAccessRequest(detail.Id);
      else await rejectRoomAccessRequest(detail.Id, reason.trim());
      toast.success(t("processed")); setSelectedId(null); setRefresh((value) => value + 1);
    } catch (err) { toast.error(err.response?.data?.Message || t("processFailed")); }
    finally { setBusy(false); }
  };

  return <div style={{ maxWidth: 1000 }}>
    <h2>{t("title")}</h2><p style={{ color: color.textSecondary }}>{t("description")}</p>
    {!isSubAccount && reminder !== null && <Card style={{ padding: 16, marginBottom: 20 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input type="checkbox" checked={reminder} disabled={settingsBusy} onChange={changeReminder} />{t("reminder")}
      </label><p style={{ marginBottom: 0, color: color.textSecondary }}>{t("reminderHelp")}</p>
    </Card>}
    {settingsError && <p role="alert">{settingsError}</p>}
    <div style={styles.controls}>
      <label>{t("filter")} <select aria-label={t("filter")} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
        {["Pending", "Approved", "Rejected", ""].map((value) => <option key={value} value={value}>{t(value || "all")}</option>)}
      </select></label>
      <button className="btn btn-secondary" onClick={() => setRefresh((value) => value + 1)} disabled={loading}>{t("refresh")}</button>
    </div>
    {loading ? <Loader /> : error ? <p role="alert">{error}</p> : rows.length === 0 ? <EmptyState text={t("empty")} /> :
      <div style={{ display: "grid", gap: 12 }}>{rows.map((row) => <Card key={row.Id} style={{ padding: 18 }}>
        <div style={styles.controls}><strong>{row.EventName}</strong><span>{t(row.Status)}</span></div>
        <p>{t("requestId")}: {row.Id} · {t("requester")}: {row.RequestingUser || row.UserId}</p>
        <p>{t("submittedName")}: {row.SubmittedEventName}</p>
        <p>{t("submittedAt")}: {formatDate(row.CreatedAt)}</p>
        <button className="btn btn-primary" onClick={() => setSelectedId(row.Id)}>{t("review")}</button>
      </Card>)}</div>}
    <nav aria-label={t("pagination")} style={{ ...styles.controls, marginTop: 18 }}>
      <button className="btn btn-secondary" disabled={loading || page === 1} onClick={() => setPage(page - 1)}>{t("previous")}</button>
      <span>{t("page", { page, total })}</span>
      <button className="btn btn-secondary" disabled={loading || page * 20 >= total} onClick={() => setPage(page + 1)}>{t("next")}</button>
    </nav>
    {selectedId !== null && <Modal title={t("reviewTitle", { id: selectedId })} maxWidth={previewOpen ? 900 : 620} busy={busy} onClose={() => !busy && setSelectedId(null)}>
      {detailError ? <p role="alert">{detailError}</p> : !detail ? <Loader /> : <>
        <dl style={styles.details}>
          <dt>{t("requester")}</dt><dd>{detail.RequestingUser || detail.UserId}</dd>
          <dt>{t("event")}</dt><dd>{detail.EventName}</dd>
          <dt>{t("submittedName")}</dt><dd>{detail.SubmittedEventName}</dd>
          <dt>{t("message")}</dt><dd>{detail.Message || "—"}</dd>
          <dt>{t("submittedAt")}</dt><dd>{formatDate(detail.CreatedAt)}</dd>
          <dt>{t("filter")}</dt><dd>{t(detail.Status)}</dd>
          {detail.ProcessedAt && <><dt>{t("processedAt")}</dt><dd>{formatDate(detail.ProcessedAt)}</dd>
            <dt>{t("processedBy")}</dt><dd>{detail.ProcessedByUserId || "—"}</dd></>}
          {detail.RejectionReason && <><dt>{t("reason")}</dt><dd>{detail.RejectionReason}</dd></>}
        </dl>
        <div style={styles.controls}>
          <button className="btn btn-primary" disabled={busy} aria-expanded={previewOpen} onClick={() => setPreviewOpen(!previewOpen)}>{t(previewOpen ? "hidePreview" : "viewDocument")}</button>
          <button className="btn btn-secondary" disabled={busy} onClick={download}>{t("download")}</button>
        </div>
        {previewOpen && <RequestDocumentPreview key={detail.Id} requestId={detail.Id} />}
        {detail.Status === "Pending" && (decision ? <div style={{ marginTop: 20 }}>
          <p>{t(decision === "approve" ? "confirmApprove" : "confirmReject")}</p>
          {decision === "reject" && <label>{t("reason")}<textarea aria-label={t("reason")} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} style={{ display: "block", width: "100%", minHeight: 80 }} /></label>}
          <div style={{ ...styles.controls, marginTop: 12 }}>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setDecision(null)}>{t("cancel")}</button>
            <button className="btn btn-primary" disabled={busy} onClick={process}>{t(busy ? "working" : "confirm")}</button>
          </div>
        </div> : <div style={{ ...styles.controls, marginTop: 20 }}>
          <button className="btn btn-danger-ghost" disabled={busy} onClick={() => setDecision("reject")}>{t("reject")}</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => setDecision("approve")}>{t("approve")}</button>
        </div>)}
      </>}
    </Modal>}
  </div>;
}
const styles = {
  controls: { display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 },
  details: { display: "grid", gridTemplateColumns: "minmax(90px, 1fr) minmax(0, 2fr)", gap: 12, overflowWrap: "anywhere", whiteSpace: "pre-wrap" },
};
